import React, { useEffect, useState } from 'react';
import useFieldTracker from '../hooks/useFieldTracker';
import VgsCollectValue from './VgsInput/VgsCollectValue';
import RawSelector from './RawSelector';
import Dob from './Dob';
import { phoneValidation } from './VgsInput/validations';
import AddressCollect from './Address/AddressCollect';
import Ownership from './Ownership';
import { FieldError } from './VgsInput/field';
import config from '../util/config';
import { generateFieldName } from '../util/fields';
import { TITLES } from '../const';

export default function KycBioInput({
  onValidChange,
  onTitleChange,
  onAddressRegionChange,
  onIsOwnerChange,
  onPercentOwnershipChange,
  form,
  askTitle,
  askIsOwner,
  askPercentOwnershipOnLoad,
  entityName,
  fieldErrors,
}: {
  onValidChange: (isValid: boolean) => void;
  onTitleChange?: (title: string) => void;
  onAddressRegionChange: (region: string) => void;
  onIsOwnerChange?: (isOwner: boolean) => void;
  onPercentOwnershipChange: (percentOwnership: number) => void;
  form: VGSForm<unknown>;
  askTitle: boolean;
  askIsOwner: boolean;
  askPercentOwnershipOnLoad: boolean;
  entityName?: string;
  fieldErrors: FieldError[];
}): React.JSX.Element {
  const rootName = generateFieldName(entityName, 'kyc');
  const firstNameName = generateFieldName(entityName, 'kycFirstName');
  const lastNameName = generateFieldName(entityName, 'kycLastName');
  const emailName = generateFieldName(entityName, 'kycEmail');
  const ssnName = generateFieldName(entityName, 'kycSsn');
  const phoneNumberName = generateFieldName(entityName, 'kycPhoneNumber');
  const addressName = generateFieldName(entityName, 'kycAddress');
  const dobName = generateFieldName(entityName, 'kycDob');
  const [emptyFields, onEmptyChange] = useFieldTracker([
    firstNameName,
    lastNameName,
    emailName,
    ssnName,
    phoneNumberName,
  ]);
  const [invalidFields, onInvalidChange] = useFieldTracker([
    addressName,
    dobName,
  ]);
  const [isValid, setIsValid] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(TITLES[0].value);

  const formattedTitles = TITLES.map((t) => ({
    ...t,
    selected: t.value === selectedTitle,
  }));

  const sharedProps = {
    required: true,
    showRequiredAsterisk: true,
    onEmptyChange,
    onInvalidChange,
    form,
    fieldErrors,
  };

  // 111-11-1111
  const ssnValidations = [/^\d{3}-\d{2}-\d{4}$/];
  if (config.environment === 'prod') {
    // if in production:
    // don't allow digits 1-3 to be 000, 666 or 900-999
    // don't allow digits 4-5 to be 00
    // don't allow digits 6-9 to be 00000
    ssnValidations.push(/^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/);
    // don't allow 123-45-6789
    ssnValidations.push(/^(?!123-45-6789).*$/);
  }

  useEffect(() => {
    const newValidity = emptyFields.length === 0 && invalidFields.length === 0;
    if (newValidity !== isValid) {
      setIsValid(newValidity);
      onValidChange(newValidity);
    }
  }, [emptyFields.length, invalidFields.length, isValid, onValidChange]);

  return (
    <form>
      <div className='md:flex mb-5 justify-between gap-2'>
        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            name={firstNameName}
            label='Legal First Name'
            type='text'
            {...sharedProps}
          />
        </div>

        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            name={lastNameName}
            label='Legal Last Name'
            type='text'
            {...sharedProps}
          />
        </div>
      </div>

      <div className='md:flex mb-5 justify-between gap-2'>
        {askTitle && (
          <div className='md:w-1/2 my-2'>
            <RawSelector
              label='Title'
              options={formattedTitles}
              onChange={(t): void => {
                if (t !== undefined) {
                  setSelectedTitle(t);
                  if (onTitleChange !== undefined) {
                    onTitleChange(t);
                  }
                }
              }}
            />
          </div>
        )}
        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            name={emailName}
            label='Email'
            type='text'
            validations={[
              /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+$/,
            ]}
            {...sharedProps}
          />
        </div>
      </div>
      <div className='md:flex mb-5 justify-between gap-2'>
        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            name={ssnName}
            label='SSN'
            type='ssn'
            placeholder='XXX-XX-XXXX'
            maskInput={{
              mask: 'XXX-XX-XXXX',
              maskChar: null,
              formatChar: { X: '[0-9]' },
            }}
            validations={ssnValidations}
            {...sharedProps}
          />
        </div>

        <div className='my-2 md:w-1/2 flex'>
          <Dob
            form={form}
            entityName={rootName}
            onInvalidChange={sharedProps.onInvalidChange}
            fieldErrors={fieldErrors}
          />
        </div>
      </div>
      <div className='md:flex mb-5'>
        <div className='md:w-full my-2'>
          <VgsCollectValue
            name={phoneNumberName}
            label='Phone Number'
            type='text'
            placeholder='(XXX) XXX-XXXX'
            maskInput={{
              mask: '(999) 999-9999',
            }}
            validations={[phoneValidation]}
            {...sharedProps}
          />
        </div>
      </div>
      <AddressCollect
        entityName={rootName}
        onRegionChange={onAddressRegionChange}
        {...sharedProps}
      />
      <Ownership
        askIsOwner={askIsOwner}
        askPercentOwnershipOnLoad={askPercentOwnershipOnLoad}
        onIsOwnerChange={onIsOwnerChange}
        onPercentOwnershipChange={onPercentOwnershipChange}
      />
    </form>
  );
}
